import { WorkContinuousRecordStore } from './work-continuous-record.store';
import { fb, goBack, routeParams, translate } from '../../../../modules/shared/shared.global-service';
import { Component, computed, effect, EventEmitter, inject, input, Output, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService } from 'xng-breadcrumb';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    ServicePatternWysiwygComponent,
} from '../../../../modules/content/components/common/service-pattern-wysiwyg/service-pattern-wysiwyg.component';
import {
    SocialServiceSelectOfferComponent,
} from '../social-service-select-offer/social-service-select-offer.component';
import { SocialServiceOperationEnum } from '@libs/types/src/services/enum/social-service-operation.enum';
import { ServicePatternStructureUsageEnum } from '@libs/types/src/services/enum/service-pattern-structure-usage.enum';
import { Validators } from '@angular/forms';
import { isInvalid } from '../../../../modules/shared/helpers/validate';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import { checkOk } from '../../../../modules/content/content.error';
import { CreateWorkInput, FolderGridItemObject, UpdateWorkInput, WorkGridItemObject } from '@it2go/types';
import {
    WorkDataContinuousRecordObject,
} from '@libs/types/src/services/object-type/work/work-data-continuous-record.object';
import { DateInputComponent } from '../../../kit/input/date-input/date-input.component';
import { WorkOfferUpsertInput } from '@libs/types/src/services/input-type/work/work-offer.upsert-input';
import { AppFormGroup, changeSignal, formAddText } from '../../../../utils/form.utils';
import { wysiwygPatterGroupsFolder } from '@libs/types/src/common/enum/wysiwyg-placeholder.enum';
import { SelectInputComponent } from '../../../kit/input/select-input/select-input.component';
import { TagsInputComponent } from '../../../kit/input/tags-input/tags-input.component';
import { EmploymentParentGroupEnum } from '@libs/types/src/user/enum/employment-parent-group.enum';
import { ServiceTypeEnum } from '@libs/types/src/services/enum/service-type.enum';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { getWorkDetailFrag } from './work-continuous-record.query';
import {
    ServiceSettingsSocialWorkType,
} from '../../../../modules/content/pages/services/settings/service-settings-social-work/service-settings-social-work.types';
import { SERVICE_SETTINGS_SOCIAL_WORK_KEY } from '@libs/types/src/services/types/common-service-settings.type';
import { offerPresenceOptions } from '../social-service-select-offer/social-service-select-offer.utils';
import {
    WorkDetailSelectServiceComponent,
} from '../../../medical-service/work/work-detail/work-detail-select-service/work-detail-select-service.component';
import {
    SelectPersonComponent,
} from '../../../../modules/content/components/common/select-person/select-person.component';
import { getFolderByPersonFrag } from '../../../medical-service/work/pages/work-visit-page/work-visit-page.query';
import { ActionButtonsComponent } from '../../../kit/action-buttons/action-buttons.component';
import { workFolderUri } from '../../folder/works/folder-works-page.component';
import {
    WorkCardContentComponent,
} from '../../../../modules/content/components/services/work-card-content/work-card-content.component';
import { ButtonComponent } from '../../../kit/button/button.component';
import { SplitterComponent } from '../../../kit/splitter/splitter.component';
import { dateifyOrNow } from '../../../../modules/shared/helpers/date.helper';
import { NgTemplateOutlet } from '@angular/common';
import { SelectUserComponent } from '../../../../modules/content/components/common/select-user/select-user.component';

@Component({
    selector: 'app-work-continuous-record',
    templateUrl: './work-continuous-record.component.html',
    styleUrl: './work-continuous-record.component.scss',
    providers: [WorkContinuousRecordStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ServicePatternWysiwygComponent,
        SocialServiceSelectOfferComponent,
        DateInputComponent,
        SelectInputComponent,
        TagsInputComponent,
        WorkDetailSelectServiceComponent,
        SelectPersonComponent,
        ActionButtonsComponent,
        WorkCardContentComponent,
        ButtonComponent,
        SplitterComponent,
        NgTemplateOutlet,
        SelectUserComponent,
    ],
})
export class WorkContinuousRecordComponent {

    public readonly store = inject(WorkContinuousRecordStore);
    public readonly router = inject(Router);
    public readonly route = inject(ActivatedRoute);
    protected readonly breadcrumbService = inject(BreadcrumbService);

    public folder = input<FolderGridItemObject | null>(null);

    protected readonly selectOfferComponent = viewChild(SocialServiceSelectOfferComponent);
    protected readonly tagsInput = viewChild<TagsInputComponent>('tagsInput');

    public readonly personIdInput = input<number | null>(null);
    @Output() closeCallback = new EventEmitter<void>();

    protected useSplitter = signal(false);
    protected existingWork: WorkGridItemObject<WorkDataContinuousRecordObject> | null = null;

    public readonly wysiwyg = viewChild<ServicePatternWysiwygComponent>(ServicePatternWysiwygComponent);

    public form = new AppFormGroup({
        serviceId: fb.control<number | null>(null, Validators.required),
        personId: fb.control<number | null>(null, Validators.required),
        userId: fb.control<string | null>(null, Validators.required),
        dateFrom: fb.control<Date | null>(new Date(), Validators.required),
        data: fb.control<string>('', { nonNullable: true }),
        form: fb.control<string | null>(null, Validators.required),
        tags: fb.control<string[]>([]),
        offers: fb.control<WorkOfferUpsertInput[]>([]),
        presence: fb.control<string | null>(null, Validators.required),
    });

    protected folderId = computed(() => this.store.data.getFolderByPerson()?.id || null);

    protected serviceIdChange = changeSignal(this.form.controls.serviceId);
    protected personIdChange = changeSignal(this.form.controls.personId);
    protected queryParams = toSignal(this.route.queryParams);

    public settings = computed(() => this.store.globalGlobalStore.organizationStore.getServiceSetting<ServiceSettingsSocialWorkType>(this.serviceIdChange() || 0, SERVICE_SETTINGS_SOCIAL_WORK_KEY));
    public presenceOptions = computed(() => offerPresenceOptions(this.settings()));

    public allowedForms = computed(() => {
        const forms = this.store.globalGlobalStore.organizationStore.getServiceById(this.serviceIdChange() || 0)?.()?.form || [];

        return forms.map((it) => ({
            name: translate.instant(`service.serviceFormEnum.${it}`),
            value: it,
        }));
    });

    private personIdFromQuery = 0;

    private actWorkItem: WorkGridItemObject<WorkDataContinuousRecordObject> | null = null;

    constructor() {
        this.personIdFromQuery = this.route.snapshot.queryParams['personId'];
        if (this.personIdFromQuery) {
            this.form.patchValue({ personId: Number(this.personIdFromQuery) }, { isCodeUpdate: true });
        }

        effect(async () => {
            const userId = this.store.globalGlobalStore.loggedUserStore.currentUserId();
            if (!userId) return;

            this.form.patchValue({ userId });
        });

        effect(() => {
            const date = this.store.workDate();
            const newDate = new Date();
            date.setHours(newDate.getHours());
            date.setMinutes(newDate.getMinutes());
            this.form.patchValue({ dateFrom: date });
        });

        effect(() => {
            const folder = this.folder();
            if (!folder) return;
            this.form.patchValue({
                serviceId: folder.serviceId,
                personId: folder.personId,
                userId: this.store.globalGlobalStore.loggedUserStore.currentUserId(),
            });
        });

        effect(() => {
            this.fetchFolderByPerson();
        });

        effect(() => {
            const workId = Number(routeParams.workId());
            if (workId) {
                this.breadcrumbService.set('@id', `id|${workId}`);
                this.store.api.getWorks(FilterBuilder.filter({ id: workId }), getWorkDetailFrag);
            }
        });

        effect(() => {
            const params = this.queryParams() || {};
            const workId = params['split'] ?? params['copy'] ?? null;
            this.useSplitter.set(!!params['split']);
            if (workId) {
                this.store.api.getWorks(FilterBuilder.filterById(workId), getWorkDetailFrag);
            }
        });

        effect(() => {
            const work = this.store.data.getWorks()?.items?.[0];
            if (!work) return;
            this.existingWork = work;

            if (!this.useSplitter()) {
                this.patchFormWithExistingWork(true);
            }
        });
    }

    public async save(): Promise<void> {
        if (isInvalid(this.form)) return;

        const _data = {
            folderId: this.folderId() || 0,
            dateFrom: this.form.value.dateFrom!.toISOString(),
            userIds: [this.form.value.userId || ''],
            tags: this.form.value.tags || [],
            workData: JSON.stringify({
                data: this.form.value.data || '',
                form: this.form.value.form,
                presence: this.form.value.presence,
            }),
            offers: this.form.value.offers || [],
        };

        let res;
        const workId = Number(routeParams.workId());
        if (workId) {
            const data = _data as UpdateWorkInput;
            data.id = Number(workId);
            res = await this.store.api.updateWork(data);
            checkOk(res, 'update');
        } else {
            const data = _data as CreateWorkInput;
            if (this.actWorkItem?.dateFrom === data.dateFrom) {
                data.dateFrom = new Date().toISOString();
            }
            data.workType = WorkTypeEnum.CONTINUOUS_RECORD;
            res = await this.store.api.createWork(data, 'id');
            checkOk(res, 'create');
        }

        this.wysiwyg()?.resetBackupValues();

        this.close();
    }

    public setData(work: WorkGridItemObject<WorkDataContinuousRecordObject>): void {
        this.actWorkItem = work;
        this.form.patchValue({
            serviceId: work.serviceId,
            personId: work.person?.id,
            dateFrom: work.dateFrom ? new Date(work.dateFrom) : new Date(),
            data: work.workData?.data || '',
            form: work.workData?.form || null,
            presence: work.workData?.presence || null,
            tags: work.tags || [],
        });
        this.tagsInput()?.setValue(this.form.value.tags || []);
        this.selectOfferComponent()?.preselectedOffers.set(work.offers || []);
    }

    public close() {
        // opened as modal
        if (this.personIdInput()) {
            this.closeCallback.emit();
            // opened from folder (as subpage)
        } else if (this.personIdFromQuery) {
            this.router.navigateByUrl(workFolderUri(WorkTypeEnum.CONTINUOUS_RECORD, this.folderId() || 0));
        } else {
            if (this.closeCallback.observed) {
                this.closeCallback.emit();
            } else {
                if (!this.folder()) {
                    goBack();
                }
            }
        }
    }

    protected queryTags(search: string): void {
        this.store.api.searchWorkTags({
            search,
            limit: 100,
            serviceId: this.form.value.serviceId || 0,
            workType: WorkTypeEnum.CONTINUOUS_RECORD,
        });
    }

    private patchFormWithExistingWork(edit: boolean = false): void {
        if (!this.existingWork) return;
        this.form.patchValue({
            serviceId: this.existingWork.serviceId,
            personId: this.existingWork.person?.id,
            userId: this.existingWork.users[0].id,
            dateFrom: dateifyOrNow(this.existingWork.dateFrom),
            form: this.existingWork.workData?.form || null,
            presence: this.existingWork.workData?.presence || null,
            tags: this.existingWork?.tags || [],
            data: this.existingWork.workData?.data || '',
        }, { isCodeUpdate: true });
        this.selectOfferComponent()?.preselectedOffers.set(this.existingWork?.offers);
        this.tagsInput()?.setValue(this.form.value.tags || []);

        if (edit) {
            this.form.patchValue({
                dateFrom: dateifyOrNow(this.existingWork.dateFrom),
            });
        }
    }

    private async fetchFolderByPerson() {
        if (!this.serviceIdChange() || !this.personIdChange()) return;

        await this.store.api.getFolderByPerson({
            serviceId: this.serviceIdChange() || 0,
            personId: this.personIdChange() || 0,
        }, getFolderByPersonFrag);
    }

    protected readonly SocialServiceOperationEnum = SocialServiceOperationEnum;
    protected readonly ServicePatternStructureUsageEnum = ServicePatternStructureUsageEnum;
    protected readonly formAddText = formAddText;
    protected readonly wysiwygPatterGroupsFolder = wysiwygPatterGroupsFolder;
    protected readonly EmploymentParentGroupEnum = EmploymentParentGroupEnum;
    protected readonly ServiceTypeEnum = ServiceTypeEnum;
    protected readonly WorkTypeEnum = WorkTypeEnum;
}
